/**
 * D-EZ-95 — WHAT HAS HAPPENED AT THIS RACETRACK.
 *
 * Two keys, because a broadcast uses both: the PHYSICAL TRACK (`venue` — the
 * same track across card years, so a 2021 card and a 2025 card of the same
 * speedway share one history), and the NAMED RACE (`raceKey`, or the event's
 * printed name when a card year changes the key). "He has won here three
 * times" is the track; "the defending winner of the Southern 500" is the race.
 */

import type { HistoryRace } from './source.ts';
import { raceRecords, type RaceRecords } from './records.ts';

export interface PastWinner {
  seasonNumber: number;
  raceNumber: number;
  raceName: string;
  driverId: string;
  name: string;
  start: number | null;
}

export interface WinsAtTrack {
  driverId: string;
  name: string;
  wins: number;
  poles: number;
  starts: number;
  bestFinish: number;
}

export interface TrackHistory {
  /** The key it was read on. */
  key: string;
  trackName: string | null;
  races: number;
  /** Most recent first. */
  winners: PastWinner[];
  /** The last man to win here (the track's or the race's), or null. */
  defendingWinner: PastWinner | null;
  /** Everyone who has raced here, most wins first. */
  byDriver: WinsAtTrack[];
  /** The biggest wrecks here, most cars first (up to five). */
  biggestWrecks: { cars: number; seasonNumber: number; raceNumber: number; raceName: string; driverIds: string[] }[];
  records: RaceRecords;
}

function build(key: string, races: readonly HistoryRace[]): TrackHistory {
  const ordered = [...races].sort((a, b) => b.leagueRaceIndex - a.leagueRaceIndex);
  const winners: PastWinner[] = [];
  const by = new Map<string, WinsAtTrack>();
  for (const r of ordered) {
    for (const l of r.lines) {
      let w = by.get(l.driverId);
      if (!w) {
        w = { driverId: l.driverId, name: l.name, wins: 0, poles: 0, starts: 0, bestFinish: l.finish };
        by.set(l.driverId, w);
      }
      w.starts += 1;
      w.bestFinish = Math.min(w.bestFinish, l.finish);
      if (l.finish === 1) w.wins += 1;
      if (l.start === 1) w.poles += 1;
    }
    const win = r.lines.find((l) => l.finish === 1);
    if (win) {
      winners.push({
        seasonNumber: r.seasonNumber,
        raceNumber: r.raceNumber,
        raceName: r.raceName,
        driverId: win.driverId,
        name: win.name,
        start: win.start,
      });
    }
  }
  const wrecks = ordered
    .filter((r) => r.biggestWreck)
    .map((r) => ({
      cars: r.biggestWreck!.cars,
      seasonNumber: r.seasonNumber,
      raceNumber: r.raceNumber,
      raceName: r.raceName,
      driverIds: r.biggestWreck!.driverIds,
    }))
    .sort((a, b) => b.cars - a.cars || b.seasonNumber - a.seasonNumber)
    .slice(0, 5);
  return {
    key,
    trackName: ordered[0]?.trackName ?? null,
    races: ordered.length,
    winners,
    defendingWinner: winners[0] ?? null,
    byDriver: [...by.values()].sort((a, b) => b.wins - a.wins || b.poles - a.poles || a.bestFinish - b.bestFinish || a.name.localeCompare(b.name)),
    biggestWrecks: wrecks,
    records: raceRecords(ordered),
  };
}

/** History at a physical track (`ScheduledRace.venue`). */
export function trackHistory(races: readonly HistoryRace[], venue: string): TrackHistory {
  return build(venue, races.filter((r) => r.venue === venue));
}

/**
 * History of a named race. Matches on `raceKey`, and also on the printed race
 * name, so the same event carries its history across card years.
 */
export function raceHistory(races: readonly HistoryRace[], race: { raceKey: string; raceName?: string }): TrackHistory {
  return build(
    race.raceKey,
    races.filter((r) => r.raceKey === race.raceKey || (race.raceName !== undefined && r.raceName === race.raceName)),
  );
}
